/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import MiniVLineChart from "./MiniVLineChart";
import VerticalLegend from "../../LegendMenu/Legend";
import { useContainerWidth } from "src/components/Hooks/useContainerWidth";
import { useContainerHeight } from "src/components/Hooks/useContainerHeight";
import { calculateYAxisDomain, formatDataLabel, getHeaderTextStyle, hasLowNSize, useResponsiveYAxisWidth, useYAxisWidth } from "./../../BarCharts/chartHelpers";
import { ChartContainer } from "./../../components/ChartContainer";
import { ChartFootnote } from "./../../components/ChartFootnote";
import { NSizeLabel } from "./../../components/NSizeLabel";
import { XAxisScale } from "./../../components/XAxisScale";
import { EllipsisText } from "./../../components/EllipsisText";
import { chartConfig } from "./../../chartConfig";
import HorizontalLegend from "./../../LegendMenu/HorizontalLegend";
import { RightAlignedEllipsisText } from "../../components/RightAlignedEllipsisText";
import ChartTabularView from "../../components/ChartTabularView";
import ChartViewToggle from "../../components/ChartViewToggle";
import { ChartRenderedFromType, IStatMarker, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { AxisLabel } from "../../components/AxisLabel";
import { pxToRem } from "src/pages/Insights/transformer/dashboardUtils";
import { useScaledConfig } from "../../ChartScaleContext";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import ChartHeader from "../../components/ChartHeader";
import ChartError from "../../ChartError";

/* -------------------- Types -------------------- */
type WaveDataPoint = {
	wave: string;
	value: number;
	n_size?: number | string;
	stat_marker?: IStatMarker | null;
};

type HierarchyGroup = {
	path: string[];
	groupKey: string;
	attributes: string[];
	attributeNSize: Record<string, { min: number | null; max: number | null }>;
	productData: Record<string, Record<string, WaveDataPoint[]>>;
	groupNSize: { min: number | null; max: number | null };
};

type HeaderCell = {
	label: string;
	colSpan: number;
	levelIndex: number;
};

type CellType = "header" | "nsize" | "chart" | "attribute" | "xaxis";

interface ICellPosition {
	rowIndex: number;
	colIndex: number;
	totalRows: number;
	totalCols: number;
	isLastInGroup: boolean;
	isFirstInGroup: boolean;
}

interface IBorderConfig {
	borderTop?: string;
	borderBottom?: string;
	borderLeft?: string;
	borderRight?: string;
}

/* -------------------- Border Helper Functions -------------------- */
const createBorderConfig = (colorsConfig: any) => {
	const solidBorder = `1px solid ${colorsConfig.borders.solid}`;
	const lightBorder = `1px solid ${colorsConfig.borders.lightGray}`;

	return {
		solid: solidBorder,
		light: lightBorder,
	};
};

const getCellBorders = (cellType: CellType, position: ICellPosition, borderConfig: { solid: string; light: string }): IBorderConfig => {
	const { rowIndex, colIndex, totalCols, totalRows, isLastInGroup } = position;
	const isLastCol = colIndex === totalCols - 1;
	const isFirstRow = rowIndex === 0;
	const isLastRow = rowIndex === totalRows - 1;

	switch (cellType) {
		case "header":
			return {
				borderTop: borderConfig.solid,
				borderRight: isLastInGroup && !isLastCol ? borderConfig.solid : undefined,
			};

		case "nsize":
			return {
				borderTop: borderConfig.solid,
				borderBottom: borderConfig.solid,
				borderRight: isLastInGroup && !isLastCol ? borderConfig.solid : undefined,
			};

		case "chart":
			return {
				borderRight: isLastInGroup && !isLastCol ? borderConfig.solid : undefined,
			};

		case "xaxis":
			return {
				borderTop: borderConfig.solid,
				// borderRight: isLastInGroup && !isLastCol ? borderConfig.solid : undefined,
			};

		case "attribute":
			return {
				// borderTop: isFirstRow ? borderConfig.solid : undefined,
				// borderBottom: isLastRow ? borderConfig.solid : borderConfig.light,
				// borderRight: borderConfig.solid,
			};

		default:
			return {};
	}
};

/* -------------------- Optimized Lazy Border Cache -------------------- */
const useBorderGetter = (colorsConfig: any, totalRows: number, totalCols: number, groupBoundaries: Set<number>) => {
	const cacheRef = useRef<Map<string, IBorderConfig>>(new Map());
	const borderConfigRef = useRef(createBorderConfig(colorsConfig));

	useEffect(() => {
		borderConfigRef.current = createBorderConfig(colorsConfig);
		cacheRef.current.clear();
	}, [colorsConfig]);

	const getBorders = useCallback(
		(cellType: CellType, rowIndex: number, colIndex: number): IBorderConfig => {
			const isLastInGroup = groupBoundaries.has(colIndex);
			const isFirstInGroup = colIndex === 0 || groupBoundaries.has(colIndex - 1);
			const key = `${cellType}:${rowIndex}:${colIndex}:${isLastInGroup}:${isFirstInGroup}:${totalRows}`;

			const cached = cacheRef.current.get(key);
			if (cached !== undefined) {
				return cached;
			}

			const position: ICellPosition = {
				rowIndex,
				colIndex,
				totalRows,
				totalCols,
				isLastInGroup,
				isFirstInGroup,
			};

			const borders = getCellBorders(cellType, position, borderConfigRef.current);
			cacheRef.current.set(key, borders);

			return borders;
		},
		[totalRows, totalCols, groupBoundaries],
	);

	return getBorders;
};

/* -------------------- Helper Functions -------------------- */


/**
 * Reorder discovered keys at a 0-based nesting depth using the authoritative
 * values from filter_hierarchy. Hierarchies are sorted by `order` ascending
 * and indexed by ordinal position. Stragglers append in discovery order.
 */
const resolveKeysByHierarchyOrder = (
	discoveredKeys: string[],
	filterHierarchy: any[] | undefined,
	depth: number,
): string[] => {
	if (!filterHierarchy || filterHierarchy.length === 0) return discoveredKeys;

	const sorted = [...filterHierarchy].sort((a, b) => a.order - b.order);
	const hAt = sorted[depth];
	if (!hAt || !Array.isArray(hAt.values) || hAt.values.length === 0) return discoveredKeys;

	const remaining = new Set(discoveredKeys);
	const ordered: string[] = [];
	hAt.values.forEach((v: string) => {
		if (remaining.has(v)) {
			ordered.push(v);
			remaining.delete(v);
		}
	});
	discoveredKeys.forEach((k) => {
		if (remaining.has(k)) ordered.push(k);
	});
	return ordered;
};

/**
 * Reorder a discovered list against an authoritative values array.
 * Stragglers append in discovery order so nothing is dropped.
 */
const reorderByValues = (discovered: string[], values: string[] | undefined): string[] => {
	if (!values || values.length === 0) return discovered;
	const remaining = new Set(discovered);
	const ordered: string[] = [];
	values.forEach((v) => {
		if (remaining.has(v)) {
			ordered.push(v);
			remaining.delete(v);
		}
	});
	discovered.forEach((d) => {
		if (remaining.has(d)) ordered.push(d);
	});
	return ordered;
};

/**
 * For vertical line chart: xAxis values become Y-axis rows (attributes)
 * and series names become the lines (waves).
 * `attributeOrderValues` orders the y-axis rows (deepest filter_hierarchy values).
 * `seriesOrder` orders the wave/series lines (from chart_config.ser_color).
 */
const processWaveArray = (
	waveArray: any[],
	currentPath: string[],
	groupKey: string,
	attributeOrderValues?: string[],
	seriesOrder?: string[],
): HierarchyGroup | null => {
	if (!Array.isArray(waveArray) || waveArray.length === 0) return null;

	const firstItem = waveArray[0];
	if (!firstItem?.xAxis || !firstItem?.series) return null;

	const attributesSet = new Set<string>();
	const wavesSet = new Set<string>();

	waveArray.forEach((item: any) => {
		if (item.xAxis) attributesSet.add(item.xAxis);
		if (item.series) {
			item.series.forEach((s: any) => {
				if (s.name) wavesSet.add(s.name);
			});
		}
	});

	// Authoritative reorderings — fall back to discovery order if no values supplied.
	const attributes = reorderByValues(Array.from(attributesSet), attributeOrderValues);
	const waves = reorderByValues(Array.from(wavesSet), seriesOrder);

	// productData structure: wave -> attribute -> WaveDataPoint[]
	const productData: Record<string, Record<string, WaveDataPoint[]>> = {};
	const attributeNSize: Record<string, { min: number | null; max: number | null }> = {};

	waves.forEach((wave) => {
		productData[wave] = {};
		attributes.forEach((attr) => {
			productData[wave][attr] = [];
		});
	});

	let groupMinN: number | null = null;
	let groupMaxN: number | null = null;

	waveArray.forEach((item: any) => {
		const attribute = item.xAxis;

		// Compute n-size range from non-zero / non-"No data" series entries so a
		// single suppressed series doesn't drag the displayed min to 0.
		const nonZeroNs: number[] = [];
		if (Array.isArray(item.series)) {
			item.series.forEach((s: any) => {
				if (s?.n_size === null || s?.n_size === undefined) return;
				if (typeof s.n_size === "string" && s.n_size.toLowerCase() === "no data") return;
				const n = Number(s.n_size);
				if (!isNaN(n) && n > 0) nonZeroNs.push(n);
			});
		}

		let attrMin: number | null = null;
		let attrMax: number | null = null;

		if (nonZeroNs.length > 0) {
			attrMin = Math.min(...nonZeroNs);
			attrMax = Math.max(...nonZeroNs);
		} else if (item.n_size) {
			const rMin = Number(item.n_size.min);
			const rMax = Number(item.n_size.max);
			attrMin = !isNaN(rMin) && rMin > 0 ? rMin : !isNaN(rMax) && rMax > 0 ? rMax : null;
			attrMax = !isNaN(rMax) && rMax > 0 ? rMax : !isNaN(rMin) && rMin > 0 ? rMin : null;
		}

		if (attrMin !== null || attrMax !== null) {
			attributeNSize[attribute] = { min: attrMin, max: attrMax };

			if (attrMin !== null) {
				groupMinN = groupMinN === null ? attrMin : Math.min(groupMinN, attrMin);
			}
			if (attrMax !== null) {
				groupMaxN = groupMaxN === null ? attrMax : Math.max(groupMaxN, attrMax);
			}
		}

		if (item.series && Array.isArray(item.series)) {
			item.series.forEach((seriesItem: any) => {
				const waveName = seriesItem.name;
				const value = seriesItem.value === "No data" ? null : seriesItem.value;

				if (value !== null && typeof value === "number" && productData[waveName]) {
					productData[waveName][attribute].push({
						wave: waveName,
						value: value,
						n_size: seriesItem.n_size === "No data" ? null : seriesItem.n_size,
						stat_marker: seriesItem.stat_marker,
					});
				}
			});
		}
	});

	return {
		path: currentPath,
		groupKey: groupKey,
		attributes,
		attributeNSize,
		productData,
		groupNSize: { min: groupMinN, max: groupMaxN },
	};
};

const isWaveArray = (arr: any): boolean => {
	return Array.isArray(arr) && arr.length > 0 && arr[0]?.xAxis !== undefined && arr[0]?.series !== undefined;
};

const buildHierarchyGroups = (
	dataNode: any,
	filterHierarchy?: any[],
	seriesOrder?: string[],
	currentPath: string[] = [],
): HierarchyGroup[] => {
	const sortedHier = filterHierarchy ? [...filterHierarchy].sort((a, b) => a.order - b.order) : [];

	if (isWaveArray(dataNode)) {
		const leafValues = sortedHier[currentPath.length]?.values;
		const group = processWaveArray(dataNode, currentPath, currentPath[currentPath.length - 1] || "Total", leafValues, seriesOrder);
		return group ? [group] : [];
	}

	if (!dataNode || typeof dataNode !== "object" || Array.isArray(dataNode)) {
		return [];
	}

	const keys = Object.keys(dataNode);
	if (keys.length === 0) return [];

	// Reorder nested keys at this depth via authoritative filter_hierarchy values.
	const orderedKeys = resolveKeysByHierarchyOrder(keys, filterHierarchy, currentPath.length);

	const groups: HierarchyGroup[] = [];
	orderedKeys.forEach((key) => {
		const value = dataNode[key];

		if (isWaveArray(value)) {
			const leafValues = sortedHier[currentPath.length + 1]?.values;
			const group = processWaveArray(value, [...currentPath, key], key, leafValues, seriesOrder);
			if (group) groups.push(group);
		} else if (value && typeof value === "object" && !Array.isArray(value)) {
			const childGroups = buildHierarchyGroups(value, filterHierarchy, seriesOrder, [...currentPath, key]);
			groups.push(...childGroups);
		}
	});

	return groups;
};

// Build header rows for column grouping (same as HLineChart)
const buildHeaderRow = (groups: HierarchyGroup[], levelIndex: number): HeaderCell[] => {
	const headers: HeaderCell[] = [];
	if (groups.length === 0) return headers;

	let currentLabel = groups[0].path[levelIndex];
	let currentSpan = 0;
	let prevGroup: HierarchyGroup | null = null;

	groups.forEach((group) => {
		const label = group.path[levelIndex];

		let parentChanged = false;

		if (levelIndex > 0 && prevGroup) {
			for (let p = 0; p < levelIndex; p++) {
				if (group.path[p] !== prevGroup.path[p]) {
					parentChanged = true;
					break;
				}
			}
		}

		if (prevGroup !== null && (label !== currentLabel || parentChanged)) {
			if (currentSpan > 0) {
				headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
			}
			currentLabel = label;
			currentSpan = 1;
		} else {
			currentSpan += 1;
		}
		prevGroup = group;
	});

	if (currentSpan > 0) {
		headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
	}

	return headers;
};

const extractAllValues = (groups: HierarchyGroup[]): number[] => {
	const values: number[] = [];
	groups.forEach((group) => {
		Object.values(group.productData).forEach((attrData) => {
			Object.values(attrData).forEach((wavePoints) => {
				wavePoints.forEach((point) => {
					if (typeof point.value === "number" && isFinite(point.value)) {
						values.push(point.value);
					}
				});
			});
		});
	});
	return values;
};

const getAllAttributes = (groups: HierarchyGroup[]): string[] => {
	if (groups.length === 0) return [];
	// All groups should have the same attributes for vertical line chart
	return groups[0].attributes;
};

const getAllWaves = (groups: HierarchyGroup[], seriesOrder?: string[]): string[] => {
	const wavesSet = new Set<string>();
	groups.forEach((group) => {
		Object.keys(group.productData).forEach((w) => wavesSet.add(w));
	});
	return reorderByValues(Array.from(wavesSet), seriesOrder);
};

/* -------------------- Main Component -------------------- */
const VLineChart: React.FC<{
	chartResponse: any;
	yAxisMinWidth: number;
	yAxisMaxWidth: number;
	yAxisWidth: number;
	chartRules?: Record<string, any>;
	isChartView: boolean;
	setIsChartView: (value: boolean) => void;
	renderedFrom?: ChartRenderedFromType;
}> = ({ chartResponse, yAxisMinWidth, yAxisMaxWidth, yAxisWidth, chartRules, isChartView, setIsChartView, renderedFrom }) => {
	const colorsConfig = chartConfig.getColorsConfig();
	const vLineChartConfig = chartConfig.getVerticalLineConfig();
	const MULTI_TILE_Y_MAX = vLineChartConfig.yAxisLabel.multiTileMaxWidth;
	const scaled = useScaledConfig();

	const apiData = chartResponse?.data?.[0]?.chart_data;
	// Determines if chart_data is missing/empty/invalid to render fallback UI
	const isDataEmpty = useMemo(() => {
		if (apiData === null || apiData === undefined) return true;
		if (Array.isArray(apiData) && apiData.length === 0) return true;
		if (typeof apiData !== "object") return true; // guard against primitives
		if (!Array.isArray(apiData) && Object.keys(apiData).length === 0) return true;
		return false;
	}, [apiData]);
	const explicitHierarchy = useMemo(() => chartResponse.filter_hierarchy || [], [chartResponse.filter_hierarchy]);
	const waveColors = useMemo(() => chartResponse?.chart_config?.ser_color || {}, [chartResponse?.chart_config?.ser_color]);
	const waveMarkers = chartResponse.chart_config.lc_marker_sp || {};
	const labelFormat = chartResponse.chart_config.dlabel_fmt || "##%";
	const lineWeight = chartResponse.chart_config.line_chart_line_weight || vLineChartConfig.lineWeight;
	const smoothLine = chartResponse.chart_config.line_smoothening === "Yes";
	const statTestConfig: IStatTestConfig | null = chartResponse.chart_config?.stat_test_config ?? null;
	const hasYAxisLabel = !!chartResponse?.y_axis_label;
	const Y_AXIS_LABEL_WIDTH = 20;
	const SCROLLBAR_WIDTH = 6;
	const ASK_KURAL_MAX_CHART_HEIGHT = 150;
	const isRenderedFromAskKural = renderedFrom === ChartRenderedFromType.AskKural;
	const isRenderedFromAskKuralInline = renderedFrom === ChartRenderedFromType.AskKuralInline;

	const minColumnWidth = chartRules?.vertical_line_chart?.chartColumnWidth?.min ?? vLineChartConfig.chartWidth.min;

	const [visibleWaves, setVisibleWaves] = useState<string[]>([]);
	const [visibleAttributes, setVisibleAttributes] = useState<string[]>([]);
	const [expandedWaves, setExpandedWaves] = useState(false);
	const [expandedAttributes, setExpandedAttributes] = useState(false);
	const [recalcKey, setRecalcKey] = useState(0);

	const containerRef = useRef<HTMLDivElement>(null);
	const contentAreaRef = useRef<HTMLDivElement>(null);
	const tableHeaderRef = useRef<HTMLDivElement>(null);
	const xAxisRef = useRef<HTMLDivElement>(null);
	const headerSectionRef = useRef<HTMLDivElement>(null);
	const yAxisLabelRef = useRef<HTMLDivElement>(null);
	const yAxisActualWidthRef = useRef<number>(0);
	const tableViewContainerRef = useRef<HTMLDivElement>(null);
	const footnoteRef = useRef<HTMLDivElement>(null);

	const containerWidth = useContainerWidth(containerRef);
	const contentAreaWidth = useContainerWidth(contentAreaRef) || containerWidth;
	const yAxisLabelWidthCalc = useContainerWidth(yAxisLabelRef);
	const xAxisHeight = useContainerHeight(xAxisRef, recalcKey);

	const rawContentAreaHeight = useContainerHeight(contentAreaRef, recalcKey);
	const stableContentAreaHeightRef = useRef<number>(0);
	const measureCountRef = useRef<number>(0);

	const contentAreaHeight = useMemo(() => {
		const raw = rawContentAreaHeight || 0;
		const prev = stableContentAreaHeightRef.current;

		if (prev === 0) {
			stableContentAreaHeightRef.current = raw;
			measureCountRef.current = 1;
			return raw;
		}

		measureCountRef.current += 1;

		if (isRenderedFromAskKuralInline && measureCountRef.current > 2) {
			return prev;
		}

		// After warm-up, require a larger delta to defeat header-wrap-induced
		// oscillation. A real resize easily exceeds this threshold.
		const threshold = measureCountRef.current > 3 ? 24 : 5;
		if (Math.abs(raw - prev) > threshold) {
			stableContentAreaHeightRef.current = raw;
			return raw;
		}

		return prev;
	}, [rawContentAreaHeight, isRenderedFromAskKuralInline]);

	const tableHeaderHeight = useContainerHeight(tableHeaderRef, recalcKey);
	const tableViewContainerHeight = useContainerHeight(tableViewContainerRef) || contentAreaHeight;
	const { isMultiTileSlide } = useSelector((state: RootState) => state.quantDashboardSlice);
	const isEffectiveMultiTile = isMultiTileSlide || isRenderedFromAskKuralInline;

	// Authoritative wave/series order from chart_config.ser_color.
	const seriesOrder = useMemo(
		() => (waveColors ? Object.keys(waveColors) : []),
		[waveColors],
	);

	const hierarchyGroups = useMemo(() => {
		if (isDataEmpty) return [];
		const groups = buildHierarchyGroups(apiData, explicitHierarchy, seriesOrder);
		return groups;
	}, [apiData, explicitHierarchy, seriesOrder, isDataEmpty]);

	// Skip hasLowNSize when data is empty — guard against undefined/empty internals
	const showLowNSize = useMemo(
		() => (isDataEmpty ? false : hasLowNSize(apiData, { threshold: 30 })),
		[apiData, isDataEmpty],
	);

	const effectiveHierarchyLevels = useMemo(() => {
		if (explicitHierarchy && explicitHierarchy.length > 0) return explicitHierarchy;
		if (hierarchyGroups.length === 0) return [];
		const maxDepth = Math.max(...hierarchyGroups.map((g) => g.path.length));
		if (maxDepth === 0) return [];
		return new Array(maxDepth).fill(null);
	}, [explicitHierarchy, hierarchyGroups]);

	// Only show hierarchy levels that have at least one non-empty label.
	const hasHierarchy = effectiveHierarchyLevels.length > 0;
	const totalColumns = hierarchyGroups.length;

	const allAttributes = useMemo(() => getAllAttributes(hierarchyGroups), [hierarchyGroups]);
	const yAxisActualWidth = useResponsiveYAxisWidth(
		allAttributes,
		yAxisWidth,
		isEffectiveMultiTile ? vLineChartConfig.yAxisLabel.minWidth : yAxisMinWidth,
		isEffectiveMultiTile ? MULTI_TILE_Y_MAX : yAxisMaxWidth,
		isEffectiveMultiTile ? scaled.scaleFactor : undefined,
	);

	const { width: resolvedYAxisWidth } = useYAxisWidth(allAttributes, containerWidth, {
		scaleFactor: scaled.scaleFactor,
		isMultiTile: isEffectiveMultiTile,
	});

	const allWaves = useMemo(() => getAllWaves(hierarchyGroups, seriesOrder), [hierarchyGroups, seriesOrder]);
	const yAxisLabelWidth = useResponsiveYAxisWidth(allAttributes);

	const isAISummaryAvailable = chartResponse.chart_config?.ai_summary_enabled ?? false;

	const groupBoundaries = useMemo(() => {
		const boundaries = new Set<number>();
		hierarchyGroups.forEach((_, idx) => {
			boundaries.add(idx);
		});
		return boundaries;
	}, [hierarchyGroups]);

	const xAxisDomain = useMemo(() => {
		const allValues = extractAllValues(hierarchyGroups);
		return calculateYAxisDomain(allValues, labelFormat);
	}, [hierarchyGroups, labelFormat]);

	const maxAttrRowHeight = chartRules?.vertical_line_chart?.attributesRowHeight ?? vLineChartConfig.minRowHeight;
	const minAttrRowHeight = vLineChartConfig.minRowHeightCompact ?? 24;

	const filteredAttributes = useMemo(() => {
		return allAttributes.filter((attr) => visibleAttributes.includes(attr));
	}, [allAttributes, visibleAttributes]);

	const needsYScroll = useMemo(() => {
		if (contentAreaHeight === 0 || filteredAttributes.length === 0) return false;
		const resolvedHeaderHeight = tableHeaderHeight || 0;
		const resolvedXAxisHeight = xAxisHeight || 32;
		const minRequiredHeight = resolvedHeaderHeight + filteredAttributes.length * minAttrRowHeight + resolvedXAxisHeight;
		const result = minRequiredHeight > contentAreaHeight;
		return result;
	}, [contentAreaHeight, filteredAttributes.length, tableHeaderHeight, xAxisHeight, minAttrRowHeight]);

	const needsScroll = useMemo(() => {
		const actualYAxisWidth = yAxisLabelWidthCalc || resolvedYAxisWidth || yAxisLabelWidth;
		const axisLabelOffset = hasYAxisLabel && !isEffectiveMultiTile ? Y_AXIS_LABEL_WIDTH : 0;
		const scrollbarOffset = needsYScroll ? SCROLLBAR_WIDTH : 0;
		const availableWidth = contentAreaWidth - actualYAxisWidth - axisLabelOffset - scrollbarOffset;
		const calculatedWidth = Math.floor(availableWidth / totalColumns);
		const result = calculatedWidth < minColumnWidth;

		return result;
	}, [contentAreaWidth, totalColumns, minColumnWidth, yAxisLabelWidth, yAxisLabelWidthCalc, resolvedYAxisWidth, hasYAxisLabel, needsYScroll, isEffectiveMultiTile]);

	// Dynamic row height - expand to fill available space
	const attrRowHeight = useMemo(() => {
		// For AskKuralInline, distribute rows evenly within the fixed cap
		if (isRenderedFromAskKuralInline && filteredAttributes.length > 0) {
			return Math.max(minAttrRowHeight, ASK_KURAL_MAX_CHART_HEIGHT / filteredAttributes.length);
		}

		if (contentAreaHeight === 0 || filteredAttributes.length === 0) return maxAttrRowHeight;

		const resolvedHeaderHeight = tableHeaderHeight || 0;
		const resolvedXAxisHeight = xAxisHeight || 32;

		const availableHeight = needsScroll
			? contentAreaHeight - resolvedHeaderHeight - resolvedXAxisHeight - 8 - 1
			: contentAreaHeight - resolvedHeaderHeight - resolvedXAxisHeight - 1;

		if (availableHeight <= 0) return maxAttrRowHeight;

		const calculatedHeight = availableHeight / filteredAttributes.length;

		return Math.max(minAttrRowHeight, calculatedHeight);
	}, [contentAreaHeight, tableHeaderHeight, filteredAttributes.length, xAxisHeight, needsScroll, isRenderedFromAskKuralInline]);

	// Chart height fills available space
	const chartHeight = useMemo(() => {
		// For AskKuralInline, use fixed cap — avoids circular dependency
		// where contentAreaHeight depends on chartHeight
		if (isRenderedFromAskKuralInline) {
			return ASK_KURAL_MAX_CHART_HEIGHT;
		}

		if (contentAreaHeight === 0) {
			return filteredAttributes.length * maxAttrRowHeight;
		}

		const resolvedHeaderHeight = tableHeaderHeight || 0;
		const resolvedXAxisHeight = xAxisHeight || 32;

		const availableHeight = needsScroll
			? contentAreaHeight - resolvedHeaderHeight - resolvedXAxisHeight - 8 - 1
			: contentAreaHeight - resolvedHeaderHeight - resolvedXAxisHeight - 1;

		if (availableHeight <= 0) return filteredAttributes.length * minAttrRowHeight;

		return Math.max(availableHeight, filteredAttributes.length * minAttrRowHeight);
	}, [contentAreaHeight, tableHeaderHeight, filteredAttributes.length, maxAttrRowHeight, xAxisHeight, minAttrRowHeight, needsScroll, isRenderedFromAskKuralInline]);

	const columnWidth = useMemo(() => {
		if (contentAreaWidth === 0 || totalColumns === 0) return minColumnWidth;

		const actualYAxisWidth = yAxisLabelWidthCalc || resolvedYAxisWidth || yAxisLabelWidth;
		const axisLabelOffset = hasYAxisLabel && !isEffectiveMultiTile ? Y_AXIS_LABEL_WIDTH : 0;
		const scrollbarOffset = needsYScroll ? SCROLLBAR_WIDTH : 0;
		const availableWidth = contentAreaWidth - actualYAxisWidth - axisLabelOffset - scrollbarOffset;

		const calculatedWidth = Math.floor(availableWidth / totalColumns);

		if (isEffectiveMultiTile) {
			return Math.max(20, calculatedWidth);
		}

		return calculatedWidth < minColumnWidth ? minColumnWidth : calculatedWidth;
	}, [contentAreaWidth, totalColumns, minColumnWidth, yAxisLabelWidth, yAxisLabelWidthCalc, resolvedYAxisWidth, hasYAxisLabel, needsYScroll, isEffectiveMultiTile]);

	const minContentWidth = useMemo(() => {
		if (!needsScroll) return contentAreaWidth;
		const actualYAxisWidth = yAxisLabelWidthCalc || yAxisLabelWidth;
		return actualYAxisWidth + totalColumns * minColumnWidth;
	}, [needsScroll, contentAreaWidth, totalColumns, minColumnWidth, yAxisLabelWidth, yAxisLabelWidthCalc]);

	const gridTemplate = useMemo(() => {
		const actualYAxisWidth = resolvedYAxisWidth;
		const yAxisCol = `${actualYAxisWidth}px`;

		if (isEffectiveMultiTile) {
			return `${yAxisCol} repeat(${totalColumns}, 1fr)`;
		}

		if (needsScroll) {
			return `${yAxisCol} repeat(${totalColumns}, ${minColumnWidth}px)`;
		}
		return `${yAxisCol} repeat(${totalColumns}, ${columnWidth}px)`;
	}, [needsScroll, totalColumns, minColumnWidth, resolvedYAxisWidth, columnWidth, isEffectiveMultiTile]);

	const getBorders = useBorderGetter(colorsConfig, filteredAttributes.length, totalColumns, groupBoundaries);

	useEffect(() => {
		setVisibleWaves(allWaves);
	}, [allWaves]);

	useEffect(() => {
		setVisibleAttributes(allAttributes);
	}, [allAttributes]);

	const showAxisColumn = !isEffectiveMultiTile || isRenderedFromAskKural;

	useEffect(() => {
		if (isChartView) {
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
			setRecalcKey((p) => p + 1);
		}
	}, [isChartView]);

	// One-shot recalc once the table header first reports a real height.
	// On initial paint, tableHeaderHeight is 0 (refs not yet measured), so
	// the chart geometry is computed against a zero header and the rows
	// render at the wrong scale. Reset the contentArea stabilization and
	// bump recalcKey so every ref re-measures against the settled layout.
	const didRecalcAfterHeaderRef = useRef(false);
	useEffect(() => {
		if (didRecalcAfterHeaderRef.current) return;
		if (tableHeaderHeight > 0) {
			didRecalcAfterHeaderRef.current = true;
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
			setRecalcKey((p) => p + 1);
		}
	}, [tableHeaderHeight]);

	if (isDataEmpty) {
		return <ChartError errorMessage="No data available" setChartData={() => { }} setIsChartLoading={() => { }} is_retry={false} />;
	}

	return (
		<ChartContainer innerRef={containerRef} renderedFrom={renderedFrom}>
			{/* HEADER: Title + Legends */}
			<div ref={headerSectionRef}>
				<ChartHeader
					title={chartResponse.chart_title}
					ai_header={chartResponse?.ai_header}
					subtitle={chartResponse.chart_subtitle}
					legendWaves={waveColors}
					visibleWaves={visibleWaves}
					setVisibleWaves={setVisibleWaves}
					expanded={expandedWaves}
					setExpanded={setExpandedWaves}
					layout="center"
					isAISummaryAvailable={isAISummaryAvailable}
					isChartView={isChartView}
					setIsChartView={setIsChartView}
					showToggle={!isEffectiveMultiTile}
					scaledTitleFontSize={pxToRem(scaled.typography.title)}
					scaledLegendFontSize={scaled.typography.legend}
					showSubtitle={true}
					showLegend={true}
					waveMarkers={waveMarkers}
					renderedFrom={renderedFrom}
				/>
			</div>

			{/* CONTENT AREA */}
			{isChartView ? (
				<div ref={contentAreaRef} className="flex flex-1 min-h-0">
					{/* Y-Axis Label */}
					{isChartView && chartResponse?.y_axis_label && !isEffectiveMultiTile && (
						<AxisLabel
							label={chartResponse?.y_axis_label || chartResponse?.chart_config?.y_lbl || "Attributes"}
							orientation="vertical"
							maxHeight={contentAreaHeight || undefined}
						/>
					)}

					<div
						className={`flex-1 relative ${needsScroll ? "overflow-x-auto" : "overflow-x-hidden"} overflow-y-auto`}>
						<div
							style={{
								minWidth: needsScroll ? `${minContentWidth}px` : undefined,
								width: "100%",
							}}>
							{/* HIERARCHY HEADERS */}
							<div ref={tableHeaderRef} className="sticky top-0 z-[1000]">
						{hasHierarchy &&
							effectiveHierarchyLevels.map((_: any, levelIndex: number) => {
								const headers = buildHeaderRow(hierarchyGroups, levelIndex);
								const isLastLevel = levelIndex === effectiveHierarchyLevels.length - 1;
								const isFirstLevel = levelIndex === 0;
								let colOffset = 0;
								return (
									<div key={levelIndex} className="grid sticky z-30 bg-white" style={{
										gridTemplateColumns: gridTemplate,
										top: `${levelIndex * scaled.layout.headerRowHeight}px`,
										minHeight: pxToRem(scaled.layout.headerRowHeight),
										width: "100%",
									}}>
										<div className="sticky left-0 z-20 bg-white" style={{ borderRight: `1px solid ${colorsConfig.borders.solid}` }} />
										{headers.map((cell, idx) => {
											const textClass = getHeaderTextStyle(levelIndex, !isFirstLevel, scaled.scaleFactor);
											colOffset += cell.colSpan;
											const endColIndex = colOffset - 1;
											const borders = getBorders("header", levelIndex, endColIndex);

											return (
												<div
													key={idx}
													className={`text-center flex flex-col items-center justify-center px-1 ${textClass.className}`}
													style={{
														gridColumn: `span ${cell.colSpan}`,
														...borders,
													}}>
													<EllipsisText text={cell.label} maxLines={isEffectiveMultiTile ? 3 : 4} className="text-center" fontSize={pxToRem(textClass.fontSize)} />
												</div>
											);
										})}
									</div>
								);
							})}

						{/* N-SIZE ROW */}
						<div className="grid sticky z-30 bg-white" style={{
							gridTemplateColumns: gridTemplate,
							top: `${effectiveHierarchyLevels.length * scaled.layout.headerRowHeight}px`,
									minHeight: pxToRem(scaled.layout.nSizeRowHeight),
									width: "100%",
								}}>
									<div className="sticky left-0 z-20 bg-white" style={{ borderRight: `1px solid ${colorsConfig.borders.solid}` }} />
									{hierarchyGroups.map((group, idx) => {
										const borders = getBorders("nsize", 0, idx);
										return (
											<div key={`nsize-${idx}`} className="flex items-center justify-center py-1 bg-white" style={borders}>
												<NSizeLabel nSize={group.groupNSize} fontSize={scaled.typography.nSize} />
											</div>
										);
									})}
								</div>
							</div>

							{/* CHART DATA AREA — row-based layout matching HClusteredBarChart body */}
							<div style={{
								minHeight: isRenderedFromAskKuralInline ? undefined : `${chartHeight}px`,
								maxHeight: isRenderedFromAskKuralInline ? `${chartHeight}px` : undefined,
								height: isRenderedFromAskKuralInline ? `${chartHeight}px` : undefined,
							}}>
								{/* Per-row grid with y-axis label + data cells */}
								{filteredAttributes.map((attr, rowIndex) => {
									const isAlt = rowIndex % 2 === 1;
									const bgColor = isAlt ? colorsConfig.backgrounds.alternate : colorsConfig.backgrounds.white;
									const cellPaddingX = isEffectiveMultiTile ? 4 : 8;
									const labelTextWidth = resolvedYAxisWidth - cellPaddingX * 2;

									return (
										<div
											key={attr}
											className="grid"
											style={{
												gridTemplateColumns: gridTemplate,
												height: `${attrRowHeight}px`,
												backgroundColor: bgColor,
											}}>
											{/* Y-axis label cell — attach ref on first row only */}
											<div
												ref={rowIndex === 0 ? yAxisLabelRef : undefined}
												className={`${isEffectiveMultiTile ? "px-1" : "px-2"} font-normal sticky left-0 z-20 flex items-center justify-end`}
												style={{
													color: colorsConfig.text.primary,
													backgroundColor: bgColor,
													borderRight: `1px solid ${colorsConfig.borders.solid}`,
													height: "inherit",
													fontSize: pxToRem(scaled.typography.yAxisLabel),
												}}>
												<RightAlignedEllipsisText
													text={attr}
													maxLines={3}
													lineHeight={scaled.typography.yAxisLabel + 3}
													maxWidth={labelTextWidth}
													className="text-end"
												/>
											</div>

											{/* Data cells — one per hierarchy group */}
											{hierarchyGroups.map((group, groupIdx) => {
												const borders = getBorders("chart", rowIndex, groupIdx);
												const isCellEmpty = !visibleWaves.some((wave) => (group.productData[wave]?.[attr]?.length ?? 0) > 0);
												const isGroupAllEmpty = !filteredAttributes.some((a) =>
													visibleWaves.some((wave) => (group.productData[wave]?.[a]?.length ?? 0) > 0)
												);

												return (
													<div
														key={`${attr}-${groupIdx}`}
														className="flex items-center justify-center overflow-hidden"
														style={{
															height: `${attrRowHeight}px`,
															...borders,
														}}>
														{isCellEmpty && !isGroupAllEmpty && (
															<span className="text-xs text-gray-400">-</span>
														)}
													</div>
												);
											})}
										</div>
									);
								})}

								{/* Chart overlay — renders line charts spanning all rows per group */}
								{filteredAttributes.length > 0 && (
									<div
										className="relative"
										style={{
											marginTop: `-${filteredAttributes.length * attrRowHeight}px`,
											height: `${filteredAttributes.length * attrRowHeight}px`,
											pointerEvents: "none",
										}}>
										<div className="grid" style={{ gridTemplateColumns: gridTemplate, height: "100%" }}>
											{/* Skip y-axis column space */}
											<div />

											{hierarchyGroups.map((group, groupIdx) => {
												const isColumnEmpty = !filteredAttributes.some((attr) =>
													visibleWaves.some((wave) => (group.productData[wave]?.[attr]?.length ?? 0) > 0)
												);

												if (isColumnEmpty) {
													return (
														<div
															key={groupIdx}
															className="flex items-center justify-center"
															style={{ height: `${filteredAttributes.length * attrRowHeight}px`, pointerEvents: "auto", width: "100%" }}>
															<span className="text-xs text-gray-400">No data</span>
														</div>
													);
												}

												return (
													<div key={groupIdx} style={{ height: `${filteredAttributes.length * attrRowHeight}px`, pointerEvents: "auto", width: "100%" }}>
														<MiniVLineChart
															key={`chart-${groupIdx}-${recalcKey}-${chartHeight}`}
															attributes={filteredAttributes}
															productData={group.productData}
															productColors={waveColors}
															waveMarkers={waveMarkers}
															visibleProducts={visibleWaves}
															visibleWaves={visibleAttributes}
															xAxisDomain={xAxisDomain}
															labelFormat={labelFormat}
															width={columnWidth}
															height={filteredAttributes.length * attrRowHeight}
															lineWeight={lineWeight}
															smoothLine={smoothLine}
															axisRight={vLineChartConfig.axisRight}
															axisLeft={vLineChartConfig.axisLeft}
															statTestConfig={statTestConfig}
															renderedFrom={renderedFrom}
															labelOverlapThreshold={isEffectiveMultiTile ? 10 : 10}
														/>
													</div>
												);
											})}
										</div>
									</div>
								)}
							</div>

							{/* X-AXIS SCALE ROW — sticky bottom like bar charts */}
							<div ref={xAxisRef} className="grid sticky bottom-[-0.5px] z-[200] bg-white" style={{ gridTemplateColumns: gridTemplate }}>
								<div className="sticky left-0 z-[200] bg-white" />
								{hierarchyGroups.map((_, idx) => {
									const borders = getBorders("xaxis", 0, idx);
									return (
										<div
											key={`xaxis-${idx}`}
											style={{
												borderTop: `1px solid ${colorsConfig.borders.solid}`,
												...borders,
											}}>
											{(!isEffectiveMultiTile || isRenderedFromAskKural) && (
												<XAxisScale
													width={columnWidth}
													paddingLeft={vLineChartConfig.axisLeft}
													paddingRight={vLineChartConfig.axisRight}
													domain={xAxisDomain}
													labelFormat={labelFormat}
													formatDataLabel={formatDataLabel}
													tickCount={2}
												/>
											)}
										</div>
									);
								})}
							</div>
						</div>
					</div>
					<div
						style={{
							width: 1,
							height: `calc(100% - ${xAxisHeight + (needsScroll ? 6 : 0)}px)`,
							minHeight: `calc(100% - ${xAxisHeight + (needsScroll ? 6 : 0)}px)`,
							borderRight: `1px solid ${colorsConfig.borders.solid}`,
							flexShrink: 0,
						}}
					/>
				</div>
			) : (
				<div
					ref={tableViewContainerRef}
					style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", justifyContent: "center", alignItems: "center" }}>
					<ChartTabularView
						chartData={apiData}
						chartType="h_cluster_bar"
						tableContainerHeight={tableViewContainerHeight}
						parentContainerWidth={containerWidth}
						labelFormat={chartResponse?.chart_config?.dlabel_fmt}
						statTestConfig={statTestConfig}
						renderedFrom={renderedFrom}
						filterHierarchy={explicitHierarchy}
						seriesOrder={seriesOrder}
					/>
				</div>
			)}

			{/* X-Axis Label */}
			{isChartView && chartResponse?.x_axis_label && !isEffectiveMultiTile && (
				<AxisLabel label={chartResponse?.x_axis_label || chartResponse?.chart_config?.x_lbl} orientation="horizontal" className="mt-2" />
			)}

			<div ref={footnoteRef}>
				<ChartFootnote
					hasLowNSize={showLowNSize}
					src={chartResponse.footnote?.src}
					footnoteObj={chartResponse.footnote}
					isWeighted={chartResponse.is_weight}
					className={`py-3 mt-auto`}
					analysis_name={chartResponse.analysis_type}
					stat_message={chartResponse.footnote?.stat_message}
					valueParams={chartResponse?.value_params}
					renderedFrom={renderedFrom}
				/>
			</div>
		</ChartContainer>
	);
};

export default VLineChart;
